import React from 'react';
import { C, PAGE_SIZE_OPTS } from './shared';

const btnBase = {
  padding: '5px 10px', background: 'transparent', color: C.primary,
  border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer',
  minWidth: 34,
};

export default function Pagination({ page, pageSize, total, loadedCount, onPageChange, onGoToPage, onPageSizeChange }) {
  const isAll = pageSize === 'all';
  const ps = isAll ? total : Number(pageSize);
  const totalPages = isAll ? 1 : (Math.ceil(total / ps) || 0);
  const from = total === 0 ? 0 : isAll ? 1 : page * ps + 1;
  const to   = isAll ? (loadedCount ?? total) : Math.min((page + 1) * ps, total);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '11px 16px', borderTop: `1px solid ${C.border}`,
      flexWrap: 'wrap', gap: 8,
    }}>
      {/* Left: counter + page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, color: C.textMd, fontWeight: 500 }}>
          {total === 0
            ? '0 results'
            : `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}
        </span>

        <select
          value={pageSize}
          onChange={e => { const v = e.target.value; onPageSizeChange(v === 'all' ? 'all' : Number(v)); }}
          style={{
            padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6,
            fontSize: 12, cursor: 'pointer', background: '#fff',
          }}
        >
          {PAGE_SIZE_OPTS.filter(v => v !== 'all' || total <= 1000).map(v => (
            <option key={v} value={v}>{v === 'all' ? 'All' : `${v} / page`}</option>
          ))}
        </select>
      </div>

      {/* Right: page buttons */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <button
            style={btnBase}
            disabled={page === 0}
            onClick={() => onGoToPage(0)}
            title="First page"
          >«</button>

          <button
            style={btnBase}
            disabled={page === 0}
            onClick={() => onPageChange(-1)}
          >‹</button>

          {/* Page number buttons with ellipsis */}
          {(() => {
            const items = [];
            const maxVisible = 7;
            let start = Math.max(0, page - Math.floor(maxVisible / 2));
            let end   = Math.min(totalPages - 1, start + maxVisible - 1);
            if (end - start < maxVisible - 1) start = Math.max(0, end - maxVisible + 1);

            if (start > 0) {
              items.push(<span key="s-dots" style={{ fontSize: 12, color: C.textSm, padding: '0 4px' }}>…</span>);
            }

            for (let i = start; i <= end; i++) {
              const pg = i;
              const active = page === i;
              items.push(
                <button
                  key={i}
                  style={{
                    ...btnBase,
                    background: active ? C.primary : 'transparent',
                    color: active ? '#fff' : C.primary,
                    borderColor: active ? C.primary : C.border,
                    fontWeight: active ? 700 : 400,
                  }}
                  onClick={() => onGoToPage(pg)}
                >
                  {i + 1}
                </button>
              );
            }

            if (end < totalPages - 1) {
              items.push(<span key="e-dots" style={{ fontSize: 12, color: C.textSm, padding: '0 4px' }}>…</span>);
            }

            return items;
          })()}

          <button
            style={btnBase}
            disabled={(page + 1) * ps >= total}
            onClick={() => onPageChange(1)}
          >›</button>

          <button
            style={btnBase}
            disabled={(page + 1) * ps >= total}
            onClick={() => onGoToPage(totalPages - 1)}
            title="Last page"
          >»</button>
        </div>
      )}
    </div>
  );
}
